/// <reference lib="dom" />
import * as d3 from "d3";
import { type ChartTheme, ChartThemeDark } from "../utils/theme.ts";

export type ChartScale = d3.ScaleContinuousNumeric<number, number>;
export type ChartGroup = d3.Selection<SVGGElement, unknown, null, undefined>;
export type ChartSvg = d3.Selection<SVGSVGElement, unknown, null, undefined>;

export interface ChartMargin {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export const ChartMarginDefault: ChartMargin = { top: 0, right: 0, bottom: 0, left: 0 };

export interface ChartInner {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

export interface ChartFrame {
  width: number;
  height: number;
  margin: ChartMargin;
  inner: ChartInner;
}

export function ChartFrameOf(width: number, height: number, margin: ChartMargin): ChartFrame {
  const left = margin.left;
  const top = margin.top;
  const right = Math.max(left + 1, width - margin.right);
  const bottom = Math.max(top + 1, height - margin.bottom);
  return {
    width,
    height,
    margin,
    inner: { left, top, right, bottom, width: right - left, height: bottom - top },
  };
}

export interface ChartCtx {
  frame: ChartFrame;
  x: ChartScale;
  baseX: ChartScale;
  y: ChartScale;
  theme: ChartTheme;
  transform: d3.ZoomTransform;
}

export interface ChartLayer {
  id: string;
  clip?: boolean;
  render(root: ChartGroup, ctx: ChartCtx): void;
}

export interface ChartSelection {
  start: number;
  end: number;
}

export interface ChartOpts {
  width?: number;
  height: number;
  margin?: Partial<ChartMargin>;
  x: ChartScale;
  y: ChartScale;
  theme?: ChartTheme;
  layers: ChartLayer[];
  zoomable?: boolean;
  maxZoom?: number;
  onSelect?: (selection: ChartSelection | null) => void;
}

export interface Chart {
  svg: ChartSvg;
  ctx(): ChartCtx;
  render(): void;
  setDomain(x?: [number, number], y?: [number, number]): void;
  setSelection(selection: ChartSelection | null): void;
  resetZoom(): void;
  resize(width: number, height?: number): void;
  destroy(): void;
}

let ChartIdSeq = 0;

export function CreateChart(container: HTMLElement, opts: ChartOpts): Chart {
  const theme = opts.theme ?? ChartThemeDark;
  const margin = { ...ChartMarginDefault, ...opts.margin };
  const clipId = `wbchart-clip-${++ChartIdSeq}`;
  const editable = !!opts.onSelect;
  const baseX = opts.x.copy();
  const y = opts.y.copy();

  let frame = ChartFrameOf(opts.width ?? (container.clientWidth || 300), opts.height, margin);
  let transform = d3.zoomIdentity;
  let selection: ChartSelection | null = null;

  const svg: ChartSvg = d3.select(container)
    .append("svg")
    .style("display", "block")
    .style("user-select", "none")
    .style("background", theme.background)
    .style("cursor", editable ? "crosshair" : opts.zoomable ? "grab" : "default");

  const clipRect = svg.append("defs").append("clipPath").attr("id", clipId).append("rect");

  const roots: ChartGroup[] = opts.layers.map((layer) =>
    svg.append("g")
      .attr("class", `layer layer-${layer.id}`)
      .attr("clip-path", layer.clip ? `url(#${clipId})` : null)
  );

  const currentX = () => transform.rescaleX(baseX);

  const ctx = (): ChartCtx => ({ frame, x: currentX(), baseX, y, theme, transform });

  const brushG = editable
    ? svg.append("g").attr("class", "brush").attr("clip-path", `url(#${clipId})`)
    : null;

  const brush = editable
    ? d3.brushX<unknown>()
      .filter((event) => !event.shiftKey && !event.button)
      .on("brush end", (event) => {
        if (!event.sourceEvent) return;
        const px = event.selection as [number, number] | null;
        const x = currentX();
        selection = px && px[1] - px[0] >= 1
          ? { start: x.invert(px[0]), end: x.invert(px[1]) }
          : null;
        if (event.type === "end") opts.onSelect!(selection);
      })
    : null;

  const zoom = opts.zoomable
    ? d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([1, opts.maxZoom ?? 1000])
      .filter((event) => {
        if (event.type === "wheel") return true;
        if (event.type === "dblclick") return false;
        return !event.button && (!editable || event.shiftKey);
      })
      .on("zoom", (event) => {
        transform = event.transform;
        render();
      })
    : null;

  function syncBrush() {
    if (!brush || !brushG) return;
    const x = currentX();
    brushG.call(brush.move, selection ? [x(selection.start), x(selection.end)] : null);
  }

  function styleBrush() {
    if (!brushG) return;
    brushG.select(".selection")
      .attr("fill", theme.selection)
      .attr("fill-opacity", theme.bandOpacity)
      .attr("stroke", theme.selection)
      .attr("stroke-opacity", theme.guideOpacity)
      .attr("stroke-dasharray", "3,2");
  }

  function layout() {
    const { inner } = frame;
    const extent: [[number, number], [number, number]] = [
      [inner.left, inner.top],
      [inner.right, inner.bottom],
    ];
    svg.attr("width", frame.width).attr("height", frame.height);
    clipRect
      .attr("x", inner.left)
      .attr("y", inner.top)
      .attr("width", inner.width)
      .attr("height", inner.height);
    baseX.range([inner.left, inner.right]);
    y.range([inner.bottom, inner.top]);
    if (zoom) zoom.extent(extent).translateExtent(extent);
    if (brush && brushG) {
      brush.extent(extent);
      brushG.call(brush);
      styleBrush();
    }
  }

  function render() {
    const c = ctx();
    opts.layers.forEach((layer, idx) => layer.render(roots[idx], c));
    syncBrush();
  }

  function resetZoom() {
    if (zoom) svg.call(zoom.transform, d3.zoomIdentity);
    else {
      transform = d3.zoomIdentity;
      render();
    }
  }

  function resize(width: number, height = frame.height) {
    if (width === frame.width && height === frame.height) return;
    frame = ChartFrameOf(width, height, margin);
    layout();
    render();
  }

  if (zoom) {
    svg.call(zoom).on("dblclick.zoom", null).on("dblclick", resetZoom);
  }

  const observer = opts.width === undefined
    ? new ResizeObserver((entries) => {
      const width = Math.floor(entries[0]?.contentRect.width ?? 0);
      if (width > 0) resize(width);
    })
    : null;
  observer?.observe(container);

  layout();
  render();

  return {
    svg,
    ctx,
    render,
    setDomain(x, yDomain) {
      if (x) baseX.domain(x);
      if (yDomain) y.domain(yDomain);
      if (x) resetZoom();
      else render();
    },
    setSelection(next) {
      selection = next;
      syncBrush();
    },
    resetZoom,
    resize,
    destroy() {
      observer?.disconnect();
      svg.remove();
    },
  };
}
